import React, { Suspense, lazy, useContext, useEffect, useRef } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Login from './Login';
import MainApp from './MainApp';
import ChangePassword from './ChangePassword';
import Loading from './Loading';
import { GlobalContext } from './context/GlobalContext';
import { getApir, getCinfo, getToken, resetSessionCache } from './lib/haxios';

// Public, shell-less full-screen wallboard for unattended TVs. Kept outside
// MainApp so it never mounts the app shell or triggers the login redirect —
// it pairs with the mobile app via a QR code instead of an operator session.
const Wallboard = lazy(() => import('./pages/wallboard/Wallboard'));

// Boot sequence:
// 1. If the user has "Remember me" on and a stored token (or just stored
//    credentials) we try to hydrate the session.
// 2. If hydration fails with an auth error AND remember-me creds exist, we
//    silently re-authenticate and retry the hydration ONCE.
// 3. On any other failure (or no remember-me) we bounce to /login.
//
// The session is validated by GET /auth/me — a single authoritative call.
// cinfo and uinfo are the same merged identity object now (company + operator).

const hydrate = async () => {
  const cinfo = await getCinfo();
  if (!cinfo) throw new Error('No session');
  const apirUrl = await getApir();
  return { cinfo, apirUrl, uinfo: cinfo };
};


const App = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { setCinfo, setUinfo, isLoading, setApir, setLoading } = useContext(GlobalContext);
  const hydratedRef = useRef(false);

  // The wallboard is a public surface — never run session hydration or the
  // login bounce for it, or a TV with no operator token would be kicked out.
  const isWallboard = pathname.startsWith('/wallboard');

  useEffect(() => {
    if (isWallboard) { setLoading(false); return; }
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const token       = localStorage.getItem('token');
    const remember    = localStorage.getItem('rememberMe') === 'true';
    const storedUser  = localStorage.getItem('rememberedUsername');
    const storedPass  = localStorage.getItem('rememberedPassword');
    const canReauth   = remember && storedUser && storedPass;

    if (!token && !canReauth) {
      setLoading(false);
      if (pathname !== '/login') nav('/login');
      return;
    }

    (async () => {
      try {
        let session = null;

        if (token) {
          try {
            session = await hydrate();
          } catch (err) {
            const status = err?.response?.status;
            // Transient / auth failure — try a silent re-auth if we can.
            if (canReauth && (status === 401 || !err?.response)) {
              console.warn('[App] token stale, attempting silent re-auth');
              const refreshed = await getToken(storedUser, storedPass, /* navigate */ false);
              if (!refreshed) throw new Error('Silent re-auth failed');
              resetSessionCache();
              session = await hydrate();
            } else {
              throw err;
            }
          }
        } else if (canReauth) {
          const refreshed = await getToken(storedUser, storedPass, false);
          if (!refreshed) throw new Error('Silent re-auth failed');
          resetSessionCache();
          session = await hydrate();
        }

        if (!session) throw new Error('No session');
        setCinfo(session.cinfo);
        setApir(session.apirUrl);
        setUinfo(session.uinfo);
        if (session.uinfo?.Inits) localStorage.setItem('userInits', session.uinfo.Inits);
        if (pathname === '/login') nav('/', { replace: true });
      } catch (e) {
        console.warn('[App] hydration failed:', e?.message || e);
        if (pathname !== '/login') nav('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Wallboard renders on its own, before the auth gate.
  if (isWallboard) {
    return (
      <Suspense fallback={<Loading/>}>
        <Routes>
          <Route path="/wallboard" element={<Wallboard/>}/>
          <Route path="/wallboard/*" element={<Wallboard/>}/>
        </Routes>
      </Suspense>
    );
  }

  if (isLoading && pathname !== '/login') return <Loading/>;

  return (
    <Routes>
      <Route path="/login" element={<Login/>}/>
      <Route path="/change-password" element={<ChangePassword/>}/>
      <Route path="/*" element={<MainApp/>}/>
    </Routes>
  );
};

export default App;
